import {
  FEATURED_SQUARE_MAX_PX,
  FEATURED_SQUARE_MIN_PX,
} from '../firestore/SlideDeckMaterial/schema'

export type ImageDimensions = { width: number; height: number }

// Decodes an image blob in the browser to read its pixel size. Used where the
// repository package handles files itself (the JSON importer); the web app's
// zod image schema has its own reader.
export const getImageDimensions = async (
  blob: Blob
): Promise<ImageDimensions> => {
  const bitmap = await createImageBitmap(blob)
  try {
    return { width: bitmap.width, height: bitmap.height }
  } finally {
    bitmap.close()
  }
}

// The same contract getFeaturedSquareSchema enforces on upload, so a JSON
// import cannot smuggle in artwork the forms would reject. Returns the problem
// as a sentence fragment, or null when the image is acceptable.
export const getFeaturedSquareDimensionError = ({
  width,
  height,
}: ImageDimensions): string | null => {
  // A zero or missing axis would make the ratio NaN or Infinity, which compares
  // false against every bound below.
  if (!width || !height) return 'could not be measured as an image'

  // Matches imageSchema, which compares the ratio rounded to two decimals.
  if ((width / height).toFixed(2) !== '1.00')
    return `must be a square (1:1) image, got ${width}x${height}`

  if (
    width < FEATURED_SQUARE_MIN_PX ||
    height < FEATURED_SQUARE_MIN_PX ||
    width > FEATURED_SQUARE_MAX_PX ||
    height > FEATURED_SQUARE_MAX_PX
  )
    return `must be between ${FEATURED_SQUARE_MIN_PX}x${FEATURED_SQUARE_MIN_PX} and ${FEATURED_SQUARE_MAX_PX}x${FEATURED_SQUARE_MAX_PX} pixels, got ${width}x${height}`

  return null
}
